import React from 'react'
import { EServerErrorMsg } from '@loc/server/constant/errorCode'
import { useLingui } from '@lingui/react/macro'
import { withLayouts } from 'react-dx'

import { globalErrorEmitter } from './events'
import {
  GlobalTrpcErrorHandler,
  parseServerErrorMessage,
  unknownErrorMessage,
} from './helpers'
import { DynamicCore } from './MessageCore/dynamic'

import type { MessageCreProps } from './MessageCore'

import { useValidInfo } from '@/hooks/usageValidation'
import { useIdleMounted } from '@/hooks/idle'
import { useSignOut } from '@/contexts/AuthContext/hooks/signOut'


import { useState, useCallback, useEffect } from 'react';
import { useConfig } from '@/contexts/SiteContext/hooks';
export interface GlobalErrorHandlerProps {}

const GlobalErrorHandler: React.FC<GlobalErrorHandlerProps> = () => {
  const [messageProps, setMessageProps] =
    useState<MessageCreProps['messageProps']>(null)
  const [initialized, setInitialized] = useState(false)
  const { supportEmail } = useConfig()
  const info = useValidInfo()
  const { uiModal, plan } = info
  const { creditsPlanIsUnlimitedAndActive } = plan

  const productName = plan?.creditsPlan?.productsSub?.productName
  const isTopPlan = productName?.toLowerCase() === 'pro'

  const idleMounted = useIdleMounted()
  const signOut = useSignOut()
  const { t } = useLingui()

  const handleServerErrorMessage = useCallback(
    (errorMessage: string) => {
      const parsedErrorMessage = parseServerErrorMessage(errorMessage)

      if (typeof parsedErrorMessage === 'string') {
        setMessageProps({
          type: 'error',
          content: parsedErrorMessage || unknownErrorMessage,
        })
        return
      }

      switch (parsedErrorMessage.message) {
        case EServerErrorMsg.USER_CREDITS_LIMIT_ERROR:
          uiModal.upgradePlan.show({
            reason: t`Get More Credits & Premium Features`,
          })
          return
        case EServerErrorMsg.USER_CONCURRENCY_EXCEED:
          if (isTopPlan) {
            uiModal.upgradeModal.show({
              action: 'upgrade-by-unlimited-reach-max-concurrent',
              appName: 'App1',
            })
          } else {
            uiModal.upgradePlan.show({
              reason: t`Concurrent task limit reached. Please wait for current tasks to finish or upgrade for more concurrent jobs.`,
            })
          }
          return
        default:
          setMessageProps({
            type: 'error',
            content: parsedErrorMessage.message,
          })
      }
    },
    [isTopPlan, t, uiModal.upgradeModal, uiModal.upgradePlan],
  )

  useEffect(() => {
    if (initialized) {
      return
    }
    if (messageProps && !initialized) {
      setInitialized(true)
    }
  }, [initialized, messageProps])

  useEffect(() => {
    const unbind = globalErrorEmitter.on('message', (message) => {
      setMessageProps({
        type: 'error',
        content: message || unknownErrorMessage,
      })
    })

    return unbind
  }, [])

  useEffect(() => {
    const unbind = globalErrorEmitter.on('server-error', (_, errorMessage) => {
      handleServerErrorMessage(errorMessage)
    })

    return unbind
  }, [handleServerErrorMessage])

  useEffect(() => {
    const trpcErrorProcessor = new GlobalTrpcErrorHandler()
    trpcErrorProcessor.use((ctx, next) => {
      const { error } = ctx

      const errorCode = error.data?.code
      if (errorCode === 'INTERNAL_SERVER_ERROR') {
        setMessageProps({ type: 'error', content: unknownErrorMessage })
      } else {
        next()
      }
    })
    trpcErrorProcessor.use((ctx, next) => {
      const { error } = ctx

      const errorCode = error.data?.code
      if (errorCode === 'INTERNAL_SERVER_ERROR') {
        setMessageProps({ type: 'error', content: unknownErrorMessage })
      } else {
        next()
      }
    })
    // 处理响应为 html 的情况
    trpcErrorProcessor.use((ctx, next) => {
      const { error } = ctx

      const response = error.meta?.response

      if (response instanceof Response) {
        const isResponseHtml = response.headers
          .get('content-type')
          ?.includes('text/html')
        // 通常是 cloudflare 拦截了用户访问
        const isForbidden = response.status === 403
        // 服务不可访问，通常是服务未正常启动，或者服务正在部署中
        const isBadGatewayError = response.status === 502
        if (isResponseHtml) {
          let msg: React.ReactNode
          if (isForbidden || isBadGatewayError) {
            msg = (
              <>
                {`We've`} updated our website. Please refresh to experience the
                new changes.&nbsp;
                <button
                  type='button'
                  onClick={() => {
                    window.location.reload()
                  }}
                  className='text-f-primary'
                >
                  Refresh Now
                </button>
              </>
            )
            setMessageProps({ type: 'success', content: msg, duration: 5 })
            return
          } else {
            msg = (
              <>
                If {`you're`} experiencing a network error with our services,
                please contact our support for help.{' '}
                <a
                  className='text-f-primary hover:underline'
                  href={`mailto:${supportEmail}`}
                >
                  Contact us
                </a>
              </>
            )
            setMessageProps({ type: 'error', content: msg, duration: 5 })
            return
          }
        }
      }
      next()
    })
    trpcErrorProcessor.use((ctx, next) => {
      const { error, parsedErrorMessage } = ctx
      if (typeof parsedErrorMessage === 'string') {
        next()
        return
      }
      handleServerErrorMessage(error.message)
    })
    // 兜底异常通知
    trpcErrorProcessor.use((ctx, next) => {
      const { parsedErrorMessage } = ctx
      if (typeof parsedErrorMessage === 'string') {
        setMessageProps({ type: 'error', content: parsedErrorMessage })
      } else {
        setMessageProps({ type: 'error', content: parsedErrorMessage.message })
      }
      next()
    })
    // 消息提示之后如果是未登录状态则强制退出
    trpcErrorProcessor.use((ctx, next) => {
      const { error } = ctx

      const errorCode = error.data?.code
      if (errorCode === 'UNAUTHORIZED') {
        signOut()
      }
      next()
    })

    const unbind = globalErrorEmitter.on('trpc-error', (error, ignore) => {
      if (ignore === true) {
        return
      }
      trpcErrorProcessor.handle(error)
    })

    return unbind
  }, [
    handleServerErrorMessage,
    creditsPlanIsUnlimitedAndActive,
    signOut,
    supportEmail,
    uiModal.upgradeModal,
    isTopPlan,
  ])

  if (idleMounted || initialized) {
    return <DynamicCore messageProps={messageProps} />
  }

  return null
}

const LayoutsGlobalErrorHandler = withLayouts(GlobalErrorHandler, [
  (props) => {
    const { sessionProviderEnabled } = useConfig()

    if (!sessionProviderEnabled) {
      return null
    }
    return <>{props.children}</>
  },
])

export default LayoutsGlobalErrorHandler
